import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography,
  Button, CircularProgress, IconButton, Grid, Tooltip, LinearProgress,
} from '@mui/material';
import {
  Close, CheckCircle, MovieFilter, MusicNote, PlayArrow, Stop,
  Piano, Diamond, Favorite, AutoAwesome, Brightness7, BubbleChart,
  Celebration, Spa, History, Stars, Download, Share, Nightlight,
  CloudUpload,
} from '@mui/icons-material';
import { merchantAuthAPI, aiGenerationAPI } from '../../../services/merchantAPI';
import { API_URLS } from '../../../config/apiConfig';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import FlutterServicesAPI from '../../../services/flutterServices';
import Cookies from 'js-cookie';

const PRIMARY_GRADIENT = 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)';
const STEPS = { LOGO: 'logo', MUSIC: 'music', CREATING: 'creating', PREVIEW: 'preview', ERROR: 'error' };

const MUSIC_ICON_MAP = {
  piano:        <Piano sx={{ fontSize: 20 }} />,
  nights_stay:  <Nightlight sx={{ fontSize: 20 }} />,
  music_note:   <MusicNote sx={{ fontSize: 20 }} />,
  wb_sunny:     <Brightness7 sx={{ fontSize: 20 }} />,
  diamond:      <Diamond sx={{ fontSize: 20 }} />,
  favorite:     <Favorite sx={{ fontSize: 20 }} />,
  auto_awesome: <AutoAwesome sx={{ fontSize: 20 }} />,
  stars:        <Stars sx={{ fontSize: 20 }} />,
  bubble_chart: <BubbleChart sx={{ fontSize: 20 }} />,
  celebration:  <Celebration sx={{ fontSize: 20 }} />,
  spa:          <Spa sx={{ fontSize: 20 }} />,
  history:      <History sx={{ fontSize: 20 }} />,
};

const TRACK_TAGS = {
  emotional_clean_guitar: ['Ambient', 'Guitar'],
  fashionable_loop: ['Upbeat', 'Fashion'],
  gentle_cloud_tone: ['Soft', 'Ambient'],
  piece_of_piano: ['Luxury', 'Piano'],
  soft_indie_guitar: ['Acoustic', 'Warm'],
};

const StepBar = ({ step }) => {
  const steps = [
    { key: STEPS.LOGO, label: 'Logo' },
    { key: STEPS.MUSIC, label: 'Music' },
    { key: STEPS.CREATING, label: 'Create' },
  ];
  const activeIdx = steps.findIndex(s => s.key === step);
  return (
    <Box display="flex" alignItems="center" gap={1} mt={1}>
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: i < activeIdx ? '#22c55e' : i === activeIdx ? '#fff' : 'rgba(255,255,255,0.35)',
              transition: 'all 0.3s',
            }} />
            <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.8)', fontFamily: 'var(--font-montserrat-medium)' }}>
              {s.label}
            </Typography>
          </Box>
          {i < steps.length - 1 && (
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.3)', mb: 1.5 }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

const ReelCreationDialog = ({ open, onClose, onReelCreated, selectedVideoIds }) => {
  const { merchant } = useMerchantAuth();
  const merchantId = merchant?.id;
  const { hasPermission } = useMerchantPermissions();
  const canShare    = hasPermission('Reels', 'share');
  const canDownload = hasPermission('Reels', 'download');

  const [step, setStep] = useState(STEPS.LOGO);
  const [brandLogos, setBrandLogos] = useState([]);
  const [logosLoading, setLogosLoading] = useState(false);
  const [selectedLogoId, setSelectedLogoId] = useState(null);

  const [musicTracks, setMusicTracks] = useState([]);
  const [musicLoading, setMusicLoading] = useState(false);
  const [selectedMusicId, setSelectedMusicId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const audioRef = useRef(null);
  const audioBlobCache = useRef({});

  const [customMusicFile, setCustomMusicFile] = useState(null);
  const fileInputRef = useRef(null);

  const [reelPrice, setReelPrice] = useState(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [creatingStep, setCreatingStep] = useState(0);  // animated progress index
  const [reelGenerationId, setReelGenerationId] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (open) {
      setStep(STEPS.LOGO);
      setSelectedLogoId(null);
      setSelectedMusicId(null);
      setCustomMusicFile(null);
      setErrorMsg('');
      setPreviewUrl(null);
      setVideoBlob(null);
      setReelGenerationId(null);
      stopPolling();
      stopAudio();
      loadBrandLogos();
      aiGenerationAPI.getReelPrice()
        .then(r => setReelPrice(r?.data?.tokens ?? null))
        .catch(() => setReelPrice(null));
    } else {
      stopAudio();
      stopPolling();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    }
  }, [open]);

  // Advance the animated "creating" steps while the reel is being built.
  useEffect(() => {
    if (step !== STEPS.CREATING) { setCreatingStep(0); return; }
    const id = setInterval(() => setCreatingStep(s => s + 1), 2200);
    return () => clearInterval(id);
  }, [step]);

  const loadBrandLogos = async () => {
    setLogosLoading(true);
    try { const r = await merchantAuthAPI.getBrandLogos(); setBrandLogos(r?.data?.brand_logos || []); }
    catch { setBrandLogos([]); } finally { setLogosLoading(false); }
  };

  const loadMusicTracks = async () => {
    setMusicLoading(true);
    try { const r = await aiGenerationAPI.getReelMusicTracks(); setMusicTracks(r?.data?.tracks || []); }
    catch { setMusicTracks([]); } finally { setMusicLoading(false); }
  };

  const stopAudio = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingId(null);
  };

  const togglePreview = async (trackId) => {
    if (playingId === trackId) { stopAudio(); return; }
    stopAudio();
    try {
      if (!audioBlobCache.current[trackId]) {
        setDownloadingId(trackId);
        // This call downloads the track on-demand on the backend (first time only)
        const r = await aiGenerationAPI.getReelMusicPreview(trackId);
        audioBlobCache.current[trackId] = URL.createObjectURL(r.data);
        setDownloadingId(null);
      }
      const audio = new Audio(audioBlobCache.current[trackId]);
      audio.volume = 0.55;
      audio.play();
      audio.onended = () => setPlayingId(null);
      audioRef.current = audio;
      setPlayingId(trackId);
    } catch {
      setDownloadingId(null);
    }
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('uploads/merchant_brand_logos/')) {
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/merchant_brand_logos/${merchantId}/${logoPath.split('/').pop()}`;
    }
    if (logoPath.startsWith('uploads/brand_logos/')) {
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/brand_logos/${logoPath.replace('uploads/brand_logos/', '')}`;
    }
    return `${API_URLS.BACKEND_BASE}/${logoPath}`;
  };

  const handleGoToMusic = () => {
    stopAudio();
    setStep(STEPS.MUSIC);
    if (!musicTracks.length) loadMusicTracks();
  };

  const handleCustomMusicChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    stopAudio();
    setCustomMusicFile(file);
    setSelectedMusicId('__custom__');
    e.target.value = '';
  };

  const handleCreateReel = async () => {
    stopAudio();
    setStep(STEPS.CREATING);
    const isCustomMusic = selectedMusicId === '__custom__';
    try {
      const res = await aiGenerationAPI.createReel(
        selectedVideoIds,
        selectedLogoId || null,
        isCustomMusic ? null : (selectedMusicId || null),
        isCustomMusic ? customMusicFile : null,
      );
      const genId = res?.data?.generation_id;
      if (!genId) throw new Error('No generation_id returned from server');
      setReelGenerationId(genId);

      // Poll status every 4 seconds until COMPLETED or FAILED
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await aiGenerationAPI.getReelStatus(genId);
          const status = statusRes?.data?.status;
          if (status === 'COMPLETED') {
            stopPolling();
            // Fetch a short-lived presigned S3 URL via authenticated endpoint (JWT stays in header)
            try {
              const urlRes = await aiGenerationAPI.getReelVideoUrl(genId);
              setPreviewUrl(urlRes?.data?.url || null);
            } catch (_urlErr) {
              setPreviewUrl(null);
            }
            setVideoBlob(null);
            setVideoLoading(true);
            setStep(STEPS.PREVIEW);
            onReelCreated?.();
          } else if (status === 'FAILED' || status === 'CANCELLED') {
            stopPolling();
            setErrorMsg('Reel creation failed on the server. Please try again.');
            setStep(STEPS.ERROR);
          }
        } catch (_pollErr) {
          // network hiccup — keep polling
        }
      }, 4000);
    } catch (err) {
      stopPolling();
      const msg = err?.response?.data?.error || err?.message || 'Failed to queue reel. Please try again.';
      setErrorMsg(msg);
      setStep(STEPS.ERROR);
    }
  };

  const handleCancelReel = async () => {
    stopPolling();
    if (reelGenerationId) {
      try { await aiGenerationAPI.cancelReel(reelGenerationId); } catch (_) {}
    }
    onClose();
  };

  const handleDownload = async () => {
    if (!reelGenerationId && !previewUrl) return;
    try {
      if (FlutterServicesAPI.isFlutterApp() && reelGenerationId) {
        const accessToken = Cookies.get('merchant_access_token');
        await FlutterServicesAPI.downloadFileToStorage({
          generationId: String(reelGenerationId),
          fileType: 'VIDEO',
          fileName: `glammify_reel_${reelGenerationId}`,
          fileExtension: 'mp4',
          downloadUrl: `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/download/${reelGenerationId}`,
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          accessToken,
        });
        return;
      }

      if (reelGenerationId) {
        const response = await aiGenerationAPI.downloadGeneration(reelGenerationId);
        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `glammify_reel_${Date.now()}.mp4`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      } else {
        window.open(previewUrl, '_blank');
      }
    } catch (_err) {
      if (previewUrl) window.open(previewUrl, '_blank');
    }
  };

  const handleShare = async () => {
    if (FlutterServicesAPI.isFlutterApp() && reelGenerationId) {
      const accessToken = Cookies.get('merchant_access_token');
      const videoStreamUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/video-stream/${reelGenerationId}`;
      const tokenizedUrl = accessToken
        ? `${videoStreamUrl}?token=${encodeURIComponent(accessToken)}`
        : videoStreamUrl;
      await window.flutter_inappwebview.callHandler('socialShare', {
        accessToken,
        url: tokenizedUrl,
        isVideo: true,
        fileName: `glammify_reel_${reelGenerationId}.mp4`,
        mimeType: 'video/mp4',
        title: 'My Jewellery Reel',
        shareMessage: '✨ Check out this jewellery reel created with Glammify!',
        text: '✨ Check out this jewellery reel created with Glammify!',
        message: '✨ Check out this jewellery reel created with Glammify!',
        multiShare: false,
        selectedItems: [],
        selectedFiles: [{
          id: String(reelGenerationId),
          url: tokenizedUrl,
          isVideo: true,
          fileName: `glammify_reel_${reelGenerationId}.mp4`,
          mimeType: 'video/mp4',
        }],
      });
      return;
    }

    if (!videoBlob) { handleDownload(); return; }
    const file = new File([videoBlob], 'glammify_reel.mp4', { type: 'video/mp4' });
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'My Jewellery Reel',
          text: '✨ Check out this jewellery reel created with Glammify!',
        });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }
    handleDownload();
  };

  const handleClose = () => {
    if (step === STEPS.CREATING) {
      handleCancelReel();
      return;
    }
    stopPolling();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>

      {/* ── Header ── */}
      <Box sx={{ background: PRIMARY_GRADIENT, px: 3, py: 1.8 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <MovieFilter sx={{ color: '#fff', fontSize: 20 }} />
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16, fontFamily: 'var(--font-montserrat-semibold)' }}>
              Create Reel
            </Typography>
          </Box>
          {step !== STEPS.CREATING && (
            <IconButton onClick={handleClose} size="small" sx={{ color: '#fff' }}><Close fontSize="small" /></IconButton>
          )}
        </Box>
        {(step === STEPS.LOGO || step === STEPS.MUSIC || step === STEPS.CREATING) && (
          <StepBar step={step} />
        )}
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5, minHeight: step === STEPS.PREVIEW ? 0 : 300 }}>

        {/* ── Step 1: Logo ── */}
        {step === STEPS.LOGO && (
          <>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 13, color: '#1E2939', mb: 0.5 }}>
              {selectedVideoIds.length} video{selectedVideoIds.length !== 1 ? 's' : ''} selected
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-medium)', fontSize: 12, color: '#6B7280', mb: 1.5 }}>
              Pick a brand logo for the end card — or skip.
            </Typography>
            {logosLoading
              ? <Box display="flex" justifyContent="center" py={3}><CircularProgress size={26} sx={{ color: '#A864ED' }} /></Box>
              : (
                <Grid container spacing={1.2}>
                  <Grid item xs={4} sm={3}>
                    <PickCard selected={selectedLogoId === null} onClick={() => setSelectedLogoId(null)} label="No Logo">
                      <Close sx={{ fontSize: 22, color: selectedLogoId === null ? '#6318C6' : '#9CA3AF' }} />
                    </PickCard>
                  </Grid>
                  {brandLogos.map(logo => (
                    <Grid item xs={4} sm={3} key={logo.id}>
                      <PickCard selected={selectedLogoId === logo.id} onClick={() => setSelectedLogoId(logo.id)} label={logo.brand_name}>
                        {getLogoUrl(logo.brand_logo_path)
                          ? <img src={getLogoUrl(logo.brand_logo_path)} alt={logo.brand_name} style={{ width: '100%', height: 46, objectFit: 'contain' }} />
                          : <Box sx={{ width: '100%', height: 46, bgcolor: '#f3f4f6', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography sx={{ fontSize: 16, color: '#9CA3AF' }}>{logo.brand_name?.[0]}</Typography>
                            </Box>
                        }
                      </PickCard>
                    </Grid>
                  ))}
                </Grid>
              )
            }
          </>
        )}

        {/* ── Step 2: Music ── */}
        {step === STEPS.MUSIC && (
          <>
            <style>{`
              @keyframes bounceBar1 {
                0%, 100% { height: 3px; }
                50% { height: 12px; }
              }
              @keyframes bounceBar2 {
                0%, 100% { height: 5px; }
                50% { height: 12px; }
              }
              @keyframes bounceBar3 {
                0%, 100% { height: 2px; }
                50% { height: 12px; }
              }
            `}</style>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              style={{ display: 'none' }}
              onChange={handleCustomMusicChange}
            />
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 13, color: '#1E2939', mb: 0.5 }}>
              Background Music
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-medium)', fontSize: 12, color: '#6B7280', mb: 2 }}>
              Pick a track, upload your own, or choose silent.
            </Typography>

            {musicLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1.5}>
                <CircularProgress size={28} sx={{ color: '#A864ED' }} />
                <Typography sx={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'var(--font-montserrat-medium)' }}>Preparing tracks…</Typography>
              </Box>
            ) : (
              <>
                {/* Silent & Custom upload quick options */}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box
                      onClick={() => {
                        stopAudio();
                        setSelectedMusicId(null);
                        setCustomMusicFile(null);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.2,
                        p: 1.2,
                        borderRadius: '10px',
                        border: selectedMusicId === null ? '2px solid #6318C6' : '1px solid #E5E7EB',
                        bgcolor: selectedMusicId === null ? '#f5f0fe' : '#FFF',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: selectedMusicId === null ? '0 4px 10px rgba(99, 24, 198, 0.06)' : 'none',
                        '&:hover': {
                          borderColor: selectedMusicId === null ? '#6318C6' : '#A864ED',
                          bgcolor: selectedMusicId === null ? '#f5f0fe' : '#faf7ff',
                        }
                      }}
                    >
                      <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: selectedMusicId === null ? '#6318C6' : '#F3F4F6',
                        color: selectedMusicId === null ? '#FFF' : '#6B7280',
                        transition: 'all 0.2s',
                        flexShrink: 0
                      }}>
                        <Close sx={{ fontSize: 14 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '12px', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: selectedMusicId === null ? '#6318C6' : '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          No Music
                        </Typography>
                        <Typography sx={{ fontSize: '9.5px', fontFamily: 'var(--font-montserrat-medium)', color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          Silent Reel
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box
                      onClick={() => {
                        stopAudio();
                        fileInputRef.current?.click();
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.2,
                        p: 1.2,
                        borderRadius: '10px',
                        border: selectedMusicId === '__custom__' ? '2px solid #6318C6' : '1px solid #E5E7EB',
                        bgcolor: selectedMusicId === '__custom__' ? '#f5f0fe' : '#FFF',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: selectedMusicId === '__custom__' ? '0 4px 10px rgba(99, 24, 198, 0.06)' : 'none',
                        '&:hover': {
                          borderColor: selectedMusicId === '__custom__' ? '#6318C6' : '#A864ED',
                          bgcolor: selectedMusicId === '__custom__' ? '#f5f0fe' : '#faf7ff',
                        }
                      }}
                    >
                      <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: selectedMusicId === '__custom__' ? '#6318C6' : '#F3F4F6',
                        color: selectedMusicId === '__custom__' ? '#FFF' : '#6B7280',
                        transition: 'all 0.2s',
                        flexShrink: 0
                      }}>
                        <CloudUpload sx={{ fontSize: 14 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '12px', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: selectedMusicId === '__custom__' ? '#6318C6' : '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          Upload Music
                        </Typography>
                        <Typography sx={{ fontSize: '9.5px', fontFamily: 'var(--font-montserrat-medium)', color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {customMusicFile ? customMusicFile.name : 'Tap to choose file'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 12.5, color: '#1E2939', mb: 1.2 }}>
                  Glammify Curated Themes
                </Typography>

                <Box sx={{
                  maxHeight: '260px',
                  overflowY: 'auto',
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: '5px' },
                  '&::-webkit-scrollbar-track': { background: '#F3F0FF', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb': { background: '#A864ED', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb:hover': { background: '#6318C6' }
                }}>
                  {musicTracks.map(t => (
                    <PlaylistRow
                      key={t.id}
                      selected={selectedMusicId === t.id}
                      onClick={() => { stopAudio(); setSelectedMusicId(t.id); setCustomMusicFile(null); }}
                      icon={MUSIC_ICON_MAP[t.icon] || <MusicNote sx={{ fontSize: 16 }} />}
                      name={t.name}
                      description={t.description}
                      tags={TRACK_TAGS[t.id] || []}
                      isPlaying={playingId === t.id}
                      isDownloading={downloadingId === t.id}
                      onPreview={() => togglePreview(t.id)}
                    />
                  ))}
                </Box>
              </>
            )}
          </>
        )}

        {/* ── Creating ── */}
        {step === STEPS.CREATING && (() => {
          const creatingSteps = [
            { label: 'Preparing your clips' },
            { label: 'Blending smooth transitions' },
            ...(selectedLogoId ? [{ label: 'Adding your brand end-card' }] : []),
            ...(selectedMusicId ? [{ label: 'Mixing in the music' }] : []),
            { label: 'Finishing your reel' },
          ];
          const activeIdx = Math.min(creatingStep, creatingSteps.length - 1);
          return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} gap={2.5}>
              {/* Animated gradient ring with film icon */}
              <Box sx={{ position: 'relative', width: 78, height: 78, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={78} thickness={2.5}
                  sx={{ color: '#A864ED', position: 'absolute', '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
                <Box sx={{
                  width: 56, height: 56, borderRadius: '50%', background: PRIMARY_GRADIENT,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  animation: 'reelPulse 1.6s ease-in-out infinite',
                  '@keyframes reelPulse': { '0%,100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.08)' } },
                }}>
                  <MovieFilter sx={{ color: '#fff', fontSize: 26 }} />
                </Box>
              </Box>

              <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 700, fontSize: 16, color: '#1E2939' }}>
                Creating your reel…
              </Typography>

              {/* Indeterminate gradient progress bar */}
              <Box sx={{ width: '78%', maxWidth: 300 }}>
                <LinearProgress sx={{
                  height: 6, borderRadius: 3, bgcolor: '#F1E9FC',
                  '& .MuiLinearProgress-bar': { borderRadius: 3, background: PRIMARY_GRADIENT },
                }} />
              </Box>

              {/* Sequential step list */}
              <Box sx={{ width: '78%', maxWidth: 300, display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                {creatingSteps.map((s, i) => {
                  const done = i < activeIdx;
                  const active = i === activeIdx;
                  return (
                    <Box key={s.label} display="flex" alignItems="center" gap={1.2} sx={{ opacity: done || active ? 1 : 0.4, transition: 'opacity 0.3s' }}>
                      <Box sx={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {done
                          ? <CheckCircle sx={{ fontSize: 18, color: '#22c55e' }} />
                          : active
                            ? <CircularProgress size={14} thickness={5} sx={{ color: '#A864ED' }} />
                            : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#D1C4E9' }} />}
                      </Box>
                      <Typography sx={{
                        fontFamily: active ? 'var(--font-montserrat-semibold)' : 'var(--font-montserrat-medium)',
                        fontSize: 12.5, color: done ? '#6B7280' : active ? '#1E2939' : '#9CA3AF',
                      }}>
                        {s.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Typography sx={{ fontFamily: 'var(--font-montserrat-medium)', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
                Hang tight — this usually takes just a few seconds.
              </Typography>
            </Box>
          );
        })()}

        {/* ── Preview ── */}
        {step === STEPS.PREVIEW && previewUrl && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 13, color: '#1E2939', mb: 1, alignSelf: 'flex-start' }}>
              Preview your reel
            </Typography>
            {/* 9:16 portrait container — matches Instagram Reels format */}
            <Box sx={{
              aspectRatio: '9 / 16',
              height: { xs: '52vh', sm: '56vh' },
              width: 'auto',
              bgcolor: '#000',
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 1.5,
              flexShrink: 0,
              position: 'relative',
            }}>
              {videoLoading && (
                <Box sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.6)', zIndex: 2,
                }}>
                  <CircularProgress size={40} sx={{ color: '#fff' }} />
                </Box>
              )}
              <video
                src={previewUrl}
                controls
                autoPlay
                loop
                playsInline
                onCanPlay={() => setVideoLoading(false)}
                onError={() => setVideoLoading(false)}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            </Box>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-medium)', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
              Happy with it? Download or share directly.
            </Typography>
          </Box>
        )}

        {/* ── Error ── */}
        {step === STEPS.ERROR && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} gap={1.5}>
            <Box sx={{ width: 52, height: 52, bgcolor: '#fee2e2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Close sx={{ fontSize: 28, color: '#ef4444' }} />
            </Box>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 700, fontSize: 15, color: '#dc2626' }}>Reel creation failed</Typography>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-medium)', fontSize: 12, color: '#6B7280', textAlign: 'center', maxWidth: 300 }}>{errorMsg}</Typography>
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      {step === STEPS.LOGO && (
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" sx={outlineSx}>Cancel</Button>
          <Button onClick={handleGoToMusic} sx={{ ...gradSx, flex: 1 }}>Next: Music →</Button>
        </DialogActions>
      )}

      {step === STEPS.MUSIC && (
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => { stopAudio(); setStep(STEPS.LOGO); }} variant="outlined" sx={outlineSx}>← Back</Button>
          <Button onClick={handleCreateReel} disabled={!selectedVideoIds.length} sx={{ ...gradSx, flex: 1 }}>
            Create Reel{reelPrice != null && reelPrice > 0 ? ` · ${reelPrice} tokens` : ''}
          </Button>
        </DialogActions>
      )}


      {step === STEPS.PREVIEW && (
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            {canShare && (
              <Button
                onClick={handleShare}
                startIcon={<Share sx={{ fontSize: 16 }} />}
                variant="outlined"
                sx={{ ...outlineSx, flex: 1, borderColor: '#A864ED', color: '#6318C6', '&:hover': { borderColor: '#6318C6', bgcolor: '#f5f0fe' } }}
              >
                Share
              </Button>
            )}
            {canDownload && (
              <Button onClick={handleDownload} startIcon={<Download sx={{ fontSize: 16 }} />} sx={{ ...gradSx, flex: 1 }}>Download</Button>
            )}
          </Box>
          <Button onClick={handleClose} variant="outlined" fullWidth sx={outlineSx}>Close</Button>
        </DialogActions>
      )}

      {step === STEPS.ERROR && (
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => setStep(STEPS.MUSIC)} variant="outlined" sx={outlineSx}>Try Again</Button>
          <Button onClick={handleClose} sx={{ ...gradSx, flex: 1 }}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

/* ─── Reusable sub-components ─────────────────────────────────── */

const PickCard = ({ selected, onClick, label, children }) => (
  <Box onClick={onClick} sx={{
    border: selected ? '2px solid #6318C6' : '2px solid #e5e7eb',
    borderRadius: '10px', p: 1, minHeight: 80,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
    cursor: 'pointer', bgcolor: selected ? '#f5f0fe' : '#fff', transition: 'all 0.2s',
    '&:hover': { borderColor: '#A864ED', bgcolor: '#faf7ff' },
  }}>
    {children}
    <Typography sx={{ fontSize: 10, fontFamily: 'var(--font-montserrat-medium)', color: selected ? '#6318C6' : '#374151', textAlign: 'center', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    {selected && <CheckCircle sx={{ fontSize: 12, color: '#6318C6' }} />}
  </Box>
);

const TrackTag = ({ label }) => (
  <Box sx={{
    display: 'inline-block',
    fontSize: '8px',
    fontFamily: 'var(--font-montserrat-semibold)',
    fontWeight: 600,
    px: 0.8,
    py: 0.1,
    borderRadius: '12px',
    bgcolor: 'rgba(168, 100, 237, 0.08)',
    color: '#8A3FFC',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  }}>
    {label}
  </Box>
);

const StaggeredEqualizer = () => (
  <Box sx={{
    display: 'flex',
    alignItems: 'flex-end',
    gap: '2px',
    height: 12,
    width: 12
  }}>
    <Box sx={{ width: 2.2, bgcolor: '#FFF', animation: 'bounceBar1 0.8s ease-in-out infinite alternate', borderRadius: '1px' }} />
    <Box sx={{ width: 2.2, bgcolor: '#FFF', animation: 'bounceBar2 0.6s ease-in-out infinite alternate', borderRadius: '1px' }} />
    <Box sx={{ width: 2.2, bgcolor: '#FFF', animation: 'bounceBar3 0.7s ease-in-out infinite alternate', borderRadius: '1px' }} />
  </Box>
);

const PlaylistRow = ({ selected, onClick, icon, name, description, tags = [], isPlaying, isDownloading, onPreview }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 1.2,
      mb: 1,
      borderRadius: '10px',
      border: selected ? '2px solid #6318C6' : '1px solid #E5E7EB',
      bgcolor: selected ? '#f5f0fe' : '#FFF',
      boxShadow: selected ? '0 4px 10px rgba(99, 24, 198, 0.06)' : 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: selected ? '#6318C6' : '#A864ED',
        bgcolor: selected ? '#f5f0fe' : '#faf7ff',
        transform: 'translateY(-1px)',
      }
    }}
  >
    <Box display="flex" alignItems="center" gap={1.2} sx={{ flex: 1, minWidth: 0 }}>
      {/* Track Icon / Equalizer */}
      <Box sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: selected 
          ? 'linear-gradient(135deg, #A864ED 0%, #6318C6 100%)' 
          : 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
        color: selected ? '#FFF' : '#6B7280',
        transition: 'all 0.2s ease',
        flexShrink: 0
      }}>
        {isPlaying ? <StaggeredEqualizer /> : icon}
      </Box>

      {/* Track Details */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box display="flex" alignItems="center" gap={0.8} flexWrap="wrap" mb={0.2}>
          <Typography sx={{
            fontSize: '12.5px',
            fontFamily: 'var(--font-montserrat-semibold)',
            fontWeight: 600,
            color: selected ? '#6318C6' : '#1F2937',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {name}
          </Typography>
          <Box display="flex" gap={0.4}>
            {tags.map(tag => (
              <TrackTag key={tag} label={tag} />
            ))}
          </Box>
        </Box>
        <Typography sx={{
          fontSize: '10px',
          fontFamily: 'var(--font-montserrat-medium)',
          color: '#888',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {description}
        </Typography>
      </Box>
    </Box>

    {/* Action Buttons */}
    <Box display="flex" alignItems="center" gap={1} sx={{ pl: 0.5 }}>
      {/* Play/Preview Button */}
      <Tooltip title={isDownloading ? 'Loading…' : isPlaying ? 'Stop' : 'Preview'} placement="top">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPreview(e);
          }}
          sx={{
            width: 26,
            height: 26,
            bgcolor: isPlaying ? '#6318C6' : '#f3f0ff',
            color: isPlaying ? '#FFF' : '#6318C6',
            '&:hover': {
              bgcolor: '#6318C6',
              color: '#FFF',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.15s ease'
          }}
        >
          {isDownloading ? (
            <CircularProgress size={10} sx={{ color: '#6318C6' }} />
          ) : isPlaying ? (
            <Stop sx={{ fontSize: 12 }} />
          ) : (
            <PlayArrow sx={{ fontSize: 12 }} />
          )}
        </IconButton>
      </Tooltip>

      {/* Selection Indicator */}
      <Box sx={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: selected ? '4px solid #6318C6' : '1px solid #D1D5DB',
        bgcolor: '#FFF',
        transition: 'all 0.15s ease',
        flexShrink: 0
      }} />
    </Box>
  </Box>
);

const gradSx = {
  borderRadius: '10px', textTransform: 'none',
  fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 700, fontSize: 13,
  background: PRIMARY_GRADIENT, color: '#fff', px: 2.5,
  '&:hover': { opacity: 0.92, background: PRIMARY_GRADIENT },
  '&:disabled': { background: '#E0E0E0', color: '#A0A0A0' },
};
const outlineSx = {
  borderRadius: '10px', textTransform: 'none',
  fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 13,
  borderColor: '#e5e7eb', color: '#374151', px: 2,
  '&:hover': { borderColor: '#9CA3AF' },
};

export default ReelCreationDialog;
